<script setup lang="ts">
const { t: $t } = useI18n()

import { joinURL, withQuery } from 'ufo'
import type { CustomerType } from '~/prisma/enums'
import type { ApiMachineryPublicBaseSelect } from '~/layers/customer-module/types'

const props = defineProps<{
  machinery?: ApiMachineryPublicBaseSelect | null
  isLoading: boolean
  isSalesView: boolean
  customerType?: CustomerType
}>()

const contacts = getContactDetails()
const contactsToUse = computed(() => props.isSalesView ? [contacts.sale] : Object.values(contacts))

// Mutations
const { $trpc, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const emailContactSales = useMutation({
  mutationFn: $trpc.customerModule.machinery.emailContactSales.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('customerModule.machinery.details.contact.emailContactSales.notification.error') }),
})

const contactFormPopup = ref(false)

interface ContactFormData {
  company?: string
  email: string
  phone?: string
  message?: string
}
function submitContactForm(data: ContactFormData) {
  if (!props.machinery) {
    return
  }

  // Send email
  emailContactSales.mutate({ id: props.machinery.id, ...data })

  // Log sales email
  useGoogleTagManager().track({
    event: 'contact_sales_email',
    category: 'machinery',
    action: 'click',
    machineryId: props.machinery.id,
  })
}

function navigateToWhatsapp() {
  const PHONE_NUMBER = '4979512970611'
  const url = withQuery(
    joinURL('https://wa.me/', PHONE_NUMBER),
    { text: $t('customerModule.machinery.details.contact.whatsappMessage', { id: props.machinery?.id }) },
  )
  navigateTo(url, { external: true })
}
</script>

<template>
  <n-card v-if="contacts && machinery">
    <n-skeleton v-if="isLoading" text width="60%" />
    <n-collapse v-else :default-expanded-names="['sale']" accordion arrow-placement="right">
      <n-collapse-item v-for="contact in contactsToUse" :key="contact.key" :name="contact.key">
        <template #header>
          <div class="flex items-center justify-between gap-2 w-full">
            <h2 class="ContactHeader">
              {{ $t(contact.titleKey) }}
            </h2>
            <Icon :name="contact.iconName" size="25" class="shrink-0 text-hos-blue" />
          </div>
        </template>

        <div v-if="(isSalesView || contact.key === 'sale') && machinery" class="mb-2 space-x-2">
          <n-button
            type="primary"
            @click="contactFormPopup = true"
          >
            <template #icon>
              <Icon name="material-symbols:mail-outline-rounded" />
            </template>
            {{ $t('customerModule.machinery.details.contact.button.request') }}
          </n-button>

          <n-button
            type="primary"
            color="green"
            @click="navigateToWhatsapp"
          >
            <template #icon>
              <Icon name="ic:outline-whatsapp" />
            </template>
            {{ $t('customerModule.machinery.details.contact.button.whatsapp') }}
          </n-button>
        </div>

        <template v-else>
          <CustomerModuleListItem
            v-if="contact.email"
            :label="$t('customerModule.machinery.details.contact.email.label')"
            :horizontal="true"
            size="md"
          >
            <NuxtLink
              :href="`mailto:${contact.email}?subject=${$t('customerModule.machinery.details.contact.email.subject', { id: machinery.id })}`"
              :external="true"
              class="underline text-hos-blue"
            >
              {{ contact.email }}
            </NuxtLink>
          </CustomerModuleListItem>
        </template>

        <CustomerModuleListItem
          v-if="contact.phone"
          :label="$t('customerModule.machinery.details.contact.phone.label')"
          :value="contact.phone"
          size="md"
          :horizontal="true"
        >
          <NuxtLink :href="`tel:${contact.phone}`" class="underline text-hos-blue">
            {{ contact.phone }}
          </NuxtLink>
        </CustomerModuleListItem>

        <CustomerModuleListItem
          v-if="isSalesView && contact.key === 'sale'"
          :label="$t('customerModule.machinery.details.contact.endCustomerSellingPrice', { currency: '€' })"
          :value="customerType === 'Dealer' ? machinery.dealerSellingPrice : machinery.endCustomerSellingPrice"
          size="md"
          :horizontal="true"
          :placeholder="$t('customerModule.machinery.details.contact.sellingPrice.placeholder')"
        />
      </n-collapse-item>
    </n-collapse>
  </n-card>

  <ThePopup
    v-if="contactFormPopup"
    :title="$t('customerModule.machinery.details.contact.contactForm.title')"
    show
    @close="contactFormPopup = false"
  >
    <n-spin :show="emailContactSales.isPending.value">
      <p v-if="emailContactSales.isSuccess.value">
        {{ $t('customerModule.machinery.details.contact.contactForm.successMessage') }}
      </p>
      <FormKit
        v-else
        type="form"
        @submit="submitContactForm"
      >
        <FormKit
          type="text"
          name="company"
          :label="$t('customerModule.machinery.details.contact.contactForm.company.label')"
        />
        <FormKit
          type="text"
          name="name"
          :label="$t('customerModule.machinery.details.contact.contactForm.name.label')"
        />
        <FormKit
          type="email"
          name="email"
          :label="$t('customerModule.machinery.details.contact.email.label')"
          validation="required"
        />
        <FormKit
          type="tel"
          name="phone"
          :label="$t('customerModule.machinery.details.contact.phone.label')"
          validation="number"
        />
        <FormKit
          type="textarea"
          name="message"
          :label="$t('customerModule.machinery.details.contact.contactForm.message.label')"
        />
      </FormKit>
    </n-spin>
  </ThePopup>
</template>

<style scoped>
.ContactHeader {
  @apply text-2xl font-semibold w-full text-hos-blue
}
</style>
